import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from '../../store/store';

import PurchaseReturnEventService from '../../services/fiscal/PurchaseReturnEventService';

import {
  addPurchaseReturnDocument,
  clearPurchaseReturnPreview,
  removePurchaseReturnDocument,
  resetPurchaseReturnEvent,
  setPurchaseReturnBusy,
  setPurchaseReturnDocumentLines,
  setPurchaseReturnError,
  setPurchaseReturnLineDiscount,
  setPurchaseReturnLineQuantity,
  setPurchaseReturnLineSelected,
  setPurchaseReturnLineUnitCost,
  setPurchaseReturnPreview,
  setPurchaseReturnPreviewLoading,
  setPurchaseReturnResult,
} from '../../store/slices/fiscal/purchaseReturnEventSlice';

import {
  PurchaseReturnDocumentCandidate,
  PurchaseReturnEligibility,
  PurchaseReturnRequest,
} from '../../types/fiscal/PurchaseReturnEvent';

import useAlerts from '../useAlerts';

// =====================================================
// 📦 Opciones
// =====================================================

interface UsePurchaseReturnEventOptions {
  isOpen?: boolean;
  initialPurchaseId?: number | null;
  onCreated?: () => void;
}

// =====================================================
// 🔑 Helpers
// =====================================================

export const purchaseReturnLineKey = (purchaseId: number, productId: number) =>
  `${purchaseId}:${productId}`;

const validatePurchaseReturnQuantity = (quantity: string, availableQuantity: string) => {
  const requested = Number(quantity);
  const available = Number(availableQuantity);
  if (!Number.isFinite(requested) || requested <= 0) return 'La cantidad debe ser mayor que cero.';
  if (!Number.isFinite(available) || requested > available) return 'La cantidad supera el saldo retornable.';
  return undefined;
};

// =====================================================
// 🪝 Hook
// =====================================================

export const usePurchaseReturnEvent = (options: UsePurchaseReturnEventOptions = {}) => {
  const { isOpen = false, initialPurchaseId, onCreated } = options;

  const dispatch: AppDispatch = useDispatch();
  const { show } = useAlerts();

  const {
    documents,
    selectedLines,
    quantities,
    unitCosts,
    discounts,

    preview,
    previewLoading,
    result,

    busy,
    error,
  } = useSelector((state: RootState) => state.purchaseReturnEvent);

  const idempotencyKey = useRef<string>();
  const submitLock = useRef(false);

  // =====================================================
  // 🧮 Request derivado
  // =====================================================

  const request = useMemo<PurchaseReturnRequest>(() => {
    const lines = documents
      .flatMap(({ eligibility }) =>
        eligibility.lines.map((line) => {
          const key = purchaseReturnLineKey(eligibility.purchaseId, line.productId);

          return {
            purchaseId: eligibility.purchaseId,
            productId: line.productId,
            quantity: Number(quantities[key] || 0),
            unitCost:
              unitCosts[key] !== undefined && unitCosts[key] !== ''
                ? Number(unitCosts[key])
                : undefined,
            discount:
              discounts[key] !== undefined && discounts[key] !== ''
                ? Number(discounts[key])
                : undefined,
            selected: Boolean(selectedLines[key]),
          };
        })
      )
      .filter((line) => line.selected && line.quantity > 0)
      .map(({ selected: _selected, ...line }) => line);

    return {
      purchaseIds: [...new Set(lines.map((line) => line.purchaseId))],
      lines,
    };
  }, [documents, quantities, unitCosts, discounts, selectedLines]);

  // =====================================================
  // ⚠️ Errores de cantidades
  // =====================================================

  const validationErrors = useMemo(() => {
    return documents.flatMap(({ eligibility }) =>
      eligibility.lines.flatMap((line) => {
        const key = purchaseReturnLineKey(eligibility.purchaseId, line.productId);

        if (!selectedLines[key]) return [];

        const validationError = validatePurchaseReturnQuantity(
          quantities[key] || '',
          String(line.availableQuantity)
        );

        return validationError ? [`${line.description}: ${validationError}`] : [];
      })
    );
  }, [documents, quantities, selectedLines]);

  const invalidatePreview = () => {
    dispatch(clearPurchaseReturnPreview());
    idempotencyKey.current = undefined;
  };

  // =====================================================
  // 📄 AGREGAR DOCUMENTO
  // =====================================================

  const handlerAddDocument = async (
    candidate?: PurchaseReturnDocumentCandidate,
    internalPurchaseId?: number
  ) => {
    const purchaseId = candidate?.purchaseId ?? internalPurchaseId;
    if (!purchaseId) return false;

    const exists = documents.some((document) => document.eligibility.purchaseId === purchaseId);
    if (exists) return false;

    dispatch(setPurchaseReturnBusy(true));
    dispatch(setPurchaseReturnError(null));

    try {
      const response = await PurchaseReturnEventService.eligibility(purchaseId);
      const eligibility = response.data;

      if (!eligibility) {
        throw new Error('No se recibió información de elegibilidad.');
      }

      if (!eligibility.eligible) {
        show(
          'warning',
          'Documento no elegible',
          eligibility.blockingReasons?.join(' ') ||
            'El documento no puede utilizarse en un Evento de Retorno.'
        );
        return false;
      }

      dispatch(addPurchaseReturnDocument({ candidate, eligibility }));
      invalidatePreview();

      return true;
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'No se pudo consultar',
        error?.response?.data?.message || 'Revise el documento e intente nuevamente.'
      );
      return false;
    } finally {
      dispatch(setPurchaseReturnBusy(false));
    }
  };

  // =====================================================
  // 🗑️ QUITAR DOCUMENTO
  // =====================================================

  const handlerRemoveDocument = (purchaseId: number) => {
    dispatch(removePurchaseReturnDocument(purchaseId));
    invalidatePreview();
  };

  // =====================================================
  // ☑️ SELECCIONAR LÍNEA
  // =====================================================

  const handlerChangeSelection = (
    purchaseId: number,
    productId: number,
    selected: boolean,
    availableQuantity: number,
    unitCost?: number,
    discount?: number
  ) => {
    const key = purchaseReturnLineKey(purchaseId, productId);

    dispatch(
      setPurchaseReturnLineSelected({
        key,
        selected,
        quantity: selected ? quantities[key] || String(availableQuantity) : '',
        unitCost: selected ? unitCosts[key] || String(unitCost ?? '') : '',
        discount: selected ? discounts[key] || String(discount ?? '') : '',
      })
    );

    invalidatePreview();
  };

  const handlerChangeQuantity = (purchaseId: number, productId: number, quantity: string) => {
    const key = purchaseReturnLineKey(purchaseId, productId);
    dispatch(setPurchaseReturnLineQuantity({ key, quantity }));
    invalidatePreview();
  };

  const handlerChangeUnitCost = (purchaseId: number, productId: number, unitCost: string) => {
    const key = purchaseReturnLineKey(purchaseId, productId);
    dispatch(setPurchaseReturnLineUnitCost({ key, unitCost }));
    invalidatePreview();
  };

  const handlerChangeDiscount = (purchaseId: number, productId: number, discount: string) => {
    const key = purchaseReturnLineKey(purchaseId, productId);
    dispatch(setPurchaseReturnLineDiscount({ key, discount }));
    invalidatePreview();
  };

  // =====================================================
  // ♻️ SELECCIONAR TODO
  // =====================================================

  const handlerSetAll = (eligibility: PurchaseReturnEligibility, selected: boolean) => {
    dispatch(
      setPurchaseReturnDocumentLines({
        purchaseId: eligibility.purchaseId,
        lines: eligibility.lines.map((line) => ({
          productId: line.productId,
          availableQuantity: line.availableQuantity,
          unitCost: line.unitCost,
          discount: line.discount,
        })),
        selected,
      })
    );

    invalidatePreview();
  };

  // =====================================================
  // 🧮 PREVIEW (silenciosa, disparada en vivo)
  // =====================================================

  const runPreview = async () => {
    dispatch(setPurchaseReturnPreviewLoading(true));

    try {
      const response = await PurchaseReturnEventService.preview(request);
      if (response.data) {
        dispatch(setPurchaseReturnPreview(response.data));
      }
    } catch {
      dispatch(setPurchaseReturnPreview(null));
    } finally {
      dispatch(setPurchaseReturnPreviewLoading(false));
    }
  };

  useEffect(() => {
    if (!request.lines.length || validationErrors.length) {
      dispatch(clearPurchaseReturnPreview());
      return;
    }

    const timer = window.setTimeout(() => {
      void runPreview();
    }, 600);

    return () => window.clearTimeout(timer);
  }, [request, validationErrors.length]);

  // =====================================================
  // 🚀 CONFIRMAR Y GENERAR (encadena borrador -> validar -> firmar -> enviar)
  // =====================================================
  // El backend de compras expone cada fase como un endpoint separado (a
  // diferencia de ventas, que hace todo en una sola llamada) para poder
  // probar cada capa manualmente. Este handler encadena las 4 llamadas para
  // que la experiencia sea la misma que en ventas: un solo clic. Si un paso
  // falla, `result` queda en el último estado exitoso y los botones de
  // retomar (Validar/Firmar/Enviar) dentro del wizard permiten continuar
  // manualmente desde ahí.

  const handlerSubmit = async () => {
    if (submitLock.current || !preview?.valid) return false;

    submitLock.current = true;
    idempotencyKey.current ||= crypto.randomUUID();

    dispatch(setPurchaseReturnBusy(true));

    try {
      const draftResponse = await PurchaseReturnEventService.saveDraft(
        request,
        idempotencyKey.current
      );
      if (!draftResponse.data) {
        throw new Error('No se recibió información del evento generado.');
      }
      dispatch(setPurchaseReturnResult(draftResponse.data));

      const validateResponse = await PurchaseReturnEventService.validate(draftResponse.data.id);
      if (!validateResponse.data) {
        throw new Error('No se recibió información del evento validado.');
      }
      dispatch(setPurchaseReturnResult(validateResponse.data));

      const signResponse = await PurchaseReturnEventService.sign(validateResponse.data.id);
      if (!signResponse.data) {
        throw new Error('No se recibió información del evento firmado.');
      }
      dispatch(setPurchaseReturnResult(signResponse.data));

      const sendResponse = await PurchaseReturnEventService.send(signResponse.data.id);
      if (!sendResponse.data) {
        throw new Error('No se recibió información del evento transmitido.');
      }
      dispatch(setPurchaseReturnResult(sendResponse.data));

      onCreated?.();
      show(sendResponse.type, sendResponse.title, sendResponse.message);

      return true;
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'No se pudo generar el evento de retorno',
        error?.response?.data?.message ||
          'El Evento de Retorno no se pudo completar. Revise el detalle e intente nuevamente.'
      );
      return false;
    } finally {
      submitLock.current = false;
      dispatch(setPurchaseReturnBusy(false));
    }
  };

  // =====================================================
  // ✅ VALIDAR (DRAFT -> VALIDATED)
  // =====================================================

  const handlerValidate = async () => {
    if (!result || submitLock.current) return false;

    submitLock.current = true;
    dispatch(setPurchaseReturnBusy(true));

    try {
      const response = await PurchaseReturnEventService.validate(result.id);

      if (!response.data) {
        throw new Error('No se recibió información del evento validado.');
      }

      dispatch(setPurchaseReturnResult(response.data));
      show(response.type, response.title, response.message);

      return true;
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'No se pudo validar',
        error?.response?.data?.message || 'El Evento de Retorno no fue validado.'
      );
      return false;
    } finally {
      submitLock.current = false;
      dispatch(setPurchaseReturnBusy(false));
    }
  };

  // =====================================================
  // ✍️ FIRMAR (VALIDATED -> SIGNED)
  // =====================================================

  const handlerSign = async () => {
    if (!result || submitLock.current) return false;

    submitLock.current = true;
    dispatch(setPurchaseReturnBusy(true));

    try {
      const response = await PurchaseReturnEventService.sign(result.id);

      if (!response.data) {
        throw new Error('No se recibió información del evento firmado.');
      }

      dispatch(setPurchaseReturnResult(response.data));
      show(response.type, response.title, response.message);

      return true;
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'No se pudo firmar',
        error?.response?.data?.message || 'El Evento de Retorno no fue firmado.'
      );
      return false;
    } finally {
      submitLock.current = false;
      dispatch(setPurchaseReturnBusy(false));
    }
  };

  // =====================================================
  // 📡 ENVIAR A HACIENDA (SIGNED -> PROCESSED/REJECTED)
  // =====================================================

  const handlerSend = async () => {
    if (!result || submitLock.current) return false;

    submitLock.current = true;
    dispatch(setPurchaseReturnBusy(true));

    try {
      const response = await PurchaseReturnEventService.send(result.id);

      if (!response.data) {
        throw new Error('No se recibió información del evento transmitido.');
      }

      dispatch(setPurchaseReturnResult(response.data));
      show(response.type, response.title, response.message);

      return true;
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'No se pudo transmitir',
        error?.response?.data?.message || 'El Evento de Retorno no fue transmitido a Hacienda.'
      );
      return false;
    } finally {
      submitLock.current = false;
      dispatch(setPurchaseReturnBusy(false));
    }
  };

  // =====================================================
  // 🧹 RESET
  // =====================================================

  const handlerResetPurchaseReturnEvent = () => {
    dispatch(resetPurchaseReturnEvent());
    idempotencyKey.current = undefined;
    submitLock.current = false;
  };

  // =====================================================
  // 🔄 Compra inicial
  // =====================================================

  useEffect(() => {
    if (isOpen && initialPurchaseId) {
      void handlerAddDocument(undefined, initialPurchaseId);
    }
  }, [isOpen, initialPurchaseId]);

  useEffect(() => {
    if (!isOpen) {
      handlerResetPurchaseReturnEvent();
    }
  }, [isOpen]);

  useEffect(() => {
    const preventUnload = (event: BeforeUnloadEvent) => {
      if (!busy) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [busy]);

  return {
    documents,
    selectedLines,
    quantities,
    unitCosts,
    discounts,

    preview,
    previewLoading,
    result,

    busy,
    error,

    request,
    validationErrors,

    purchaseReturnLineKey,

    handlerAddDocument,
    handlerRemoveDocument,

    handlerChangeSelection,
    handlerChangeQuantity,
    handlerChangeUnitCost,
    handlerChangeDiscount,
    handlerSetAll,

    handlerSubmit,
    handlerValidate,
    handlerSign,
    handlerSend,
    handlerResetPurchaseReturnEvent,

    invalidatePreview,
  };
};

export default usePurchaseReturnEvent;
